/**
 * Shared building blocks for the pool onboarding wizards
 * (new-private-pool, on-demand-access). These flows target business/sales
 * users: one decision per step, plain language, safe defaults.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { orgOptionLabels } from '../../lib/orgLabels';
import styles from './PoolWizards.module.css';

export function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

export interface WizardOrg {
  id: string;
  name: string;
  deactivatedAt: string | null;
}

export interface WizardProject {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className={styles.stepper} aria-label="Progress">
      {steps.map((label, i) => (
        <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {i > 0 && <span className={styles.stepperDivider} aria-hidden="true" />}
          <span
            className={
              i === current ? styles.stepperItemCurrent :
              i < current ? styles.stepperItemDone :
              styles.stepperItem
            }
            aria-current={i === current ? 'step' : undefined}
          >
            <span className={styles.stepperIndex}>{i < current ? '✓' : i + 1}</span>
            {label}
          </span>
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer picker (organization → project), used as step 1 of both wizards
// ---------------------------------------------------------------------------

export interface CustomerSelection {
  orgId: string;
  orgName: string;
  projectId: string;
  projectName: string;
}

export function CustomerPicker({
  authHeaders,
  value,
  onChange,
}: {
  authHeaders: () => Promise<Record<string, string>>;
  value: CustomerSelection;
  onChange: (next: CustomerSelection) => void;
}) {
  const [orgs, setOrgs] = useState<WizardOrg[]>([]);
  const orgLabels = useMemo(() => orgOptionLabels(orgs), [orgs]);
  const [projects, setProjects] = useState<WizardProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as { organizations: WizardOrg[] };
        if (cancelled) return;
        const activeOrgs = (data.organizations ?? [])
          .filter(o => !o.deactivatedAt)
          .sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(activeOrgs);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load organizations');
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const loadProjects = useCallback(async (orgId: string) => {
    setLoadingProjects(true);
    setProjects([]);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(orgId)}/projects`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { projects: WizardProject[] };
      setProjects(data.projects ?? []);
      return data.projects ?? [];
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      return [];
    } finally {
      setLoadingProjects(false);
    }
  }, [authHeaders]);

  const handleOrgChange = useCallback(async (orgId: string) => {
    const org = orgs.find(o => o.id === orgId);
    onChange({ orgId, orgName: org?.name ?? '', projectId: '', projectName: '' });
    if (!orgId) return;
    const loaded = await loadProjects(orgId);
    // Auto-select when there's exactly one project — one less click.
    if (loaded.length === 1) {
      onChange({ orgId, orgName: org?.name ?? '', projectId: loaded[0].id, projectName: loaded[0].name });
    }
  }, [orgs, onChange, loadProjects]);

  return (
    <div>
      {error && <div className={styles.errorBox}>{error}</div>}
      <label className={styles.fieldLabel} htmlFor="wizard-org">Organization</label>
      <select
        id="wizard-org"
        className={styles.select}
        value={value.orgId}
        onChange={e => handleOrgChange(e.target.value)}
      >
        <option value="">Choose an organization…</option>
        {orgs.map(o => <option key={o.id} value={o.id}>{orgLabels.get(o.id) ?? o.name}</option>)}
      </select>

      {value.orgId && (
        <>
          <label className={styles.fieldLabel} htmlFor="wizard-project">Project</label>
          <select
            id="wizard-project"
            className={styles.select}
            value={value.projectId}
            onChange={e => {
              const p = projects.find(pr => pr.id === e.target.value);
              onChange({ ...value, projectId: e.target.value, projectName: p?.name ?? '' });
            }}
            disabled={loadingProjects}
          >
            {loadingProjects ? (
              <option value="">Loading projects…</option>
            ) : projects.length === 0 ? (
              <option value="">No projects in this organization</option>
            ) : (
              <>
                <option value="">Choose a project…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </>
            )}
          </select>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard footer actions
// ---------------------------------------------------------------------------

export function WizardActions({
  onBack,
  onNext,
  nextLabel = 'Next',
  nextDisabled = false,
  backDisabled = false,
}: {
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  backDisabled?: boolean;
}) {
  return (
    <div className={styles.actions}>
      {onBack ? (
        <button type="button" className={styles.secondaryButton} onClick={onBack} disabled={backDisabled}>
          Back
        </button>
      ) : <span />}
      <button type="button" className={styles.primaryButton} onClick={onNext} disabled={nextDisabled}>
        {nextLabel}
      </button>
    </div>
  );
}
